import { X, Sparkle, EnvelopeSimple, WhatsappLogo, Printer, PaperPlaneTilt, DownloadSimple } from "@phosphor-icons/react";
import { useOutreach } from "./outreach/useOutreach";
import PostalAddressFields from "./outreach/PostalAddressFields";

const CHANNELS = [
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo },
  { key: "courrier", label: "Courrier", icon: Printer },
];
const INTENTS = [
  { key: "premier_contact", label: "Premier contact" },
  { key: "relance", label: "Relance" },
  { key: "rdv", label: "Demande de RDV" },
  { key: "proposition_loi", label: "Proposition LOI" },
  { key: "nda", label: "Proposition NDA" },
];

export default function OutreachModal({ company, onClose, onLogged, initialBody }) {
  const o = useOutreach({ company, onClose, onLogged, initialBody });
  const { channel, setChannel, intent, setIntent, subject, setSubject, body, setBody,
    to, setTo, phone, setPhone, drafting, sending, mode, setMode,
    exp, dest, setExpF, setDestF, draft, sendEmail, sendWhatsapp, downloadPdf, sendPostal } = o;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outreach-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-border w-full max-w-2xl max-h-[88vh] flex flex-col fade-up">
        <div className="h-14 px-5 flex items-center justify-between border-b border-border shrink-0">
          <div className="font-head font-bold">Contacter · {company.nom}</div>
          <button onClick={onClose} data-testid="outreach-close"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className="flex gap-2 mb-4">
            {CHANNELS.map((ch) => (
              <button key={ch.key} onClick={() => setChannel(ch.key)} data-testid={`outreach-channel-${ch.key}`}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium border transition-colors ${channel === ch.key ? "border-primary bg-accent text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                <ch.icon size={16} /> {ch.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2 mb-4">
            <select value={intent} onChange={(e) => setIntent(e.target.value)} data-testid="outreach-intent"
              className="flex-1 px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              {INTENTS.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
            </select>
            <button onClick={draft} disabled={drafting} data-testid="outreach-draft-btn"
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[#EEF2FF] text-[#4f46e5] border border-[#c7d2fe] hover:bg-[#e0e7ff] disabled:opacity-60">
              <Sparkle size={15} weight="fill" /> {drafting ? "Rédaction…" : "Rédiger avec l'IA"}
            </button>
          </div>

          {channel === "email" && (
            <input data-testid="outreach-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Email du destinataire"
              className="w-full mb-3 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
          )}
          {channel === "whatsapp" && (
            <input data-testid="outreach-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numéro (ex: 33612345678)"
              className="w-full mb-3 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
          )}

          {channel !== "whatsapp" && (
            <input data-testid="outreach-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet"
              className="w-full mb-3 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
          )}

          <textarea data-testid="outreach-body" value={body} onChange={(e) => setBody(e.target.value)} rows={channel === "courrier" ? 6 : 10}
            placeholder="Votre message… ou cliquez sur « Rédiger avec l'IA »"
            className="w-full px-3 py-2.5 text-sm border border-border outline-none focus:border-primary font-sans" />

          {channel === "courrier" && (
            <PostalAddressFields exp={exp} dest={dest} setExpF={setExpF} setDestF={setDestF} />
          )}
        </div>

        <div className="px-5 py-3 border-t border-border shrink-0 flex items-center justify-end gap-2">
          {channel === "email" && (
            <button onClick={sendEmail} disabled={sending} data-testid="outreach-send-email"
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-60">
              <PaperPlaneTilt size={15} weight="fill" /> {sending ? "Envoi…" : "Envoyer l'email"}
            </button>
          )}
          {channel === "whatsapp" && (
            <button onClick={sendWhatsapp} data-testid="outreach-send-whatsapp"
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-[#25D366] text-white hover:opacity-90">
              <WhatsappLogo size={16} weight="fill" /> Ouvrir WhatsApp
            </button>
          )}
          {channel === "courrier" && (
            <>
              <select data-testid="postal-mode" value={mode} onChange={(e) => setMode(e.target.value)}
                className="px-2 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary mr-auto">
                <option value="normal">Lettre verte</option>
                <option value="suivi">Suivi</option>
                <option value="lrar">Recommandé AR</option>
              </select>
              <button onClick={downloadPdf} disabled={sending} data-testid="outreach-download-pdf"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-60">
                <DownloadSimple size={15} /> PDF
              </button>
              <button onClick={sendPostal} disabled={sending} data-testid="outreach-send-postal"
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-60">
                <Printer size={15} /> {sending ? "Envoi…" : "Envoyer (Merci Facteur)"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
